/**
 * this helper does the following:
 *   - fetches directus content from api
 *   - validates directus content for requested fields
 *     - sends validate error to sentry
 *     - sets invalid directus content to (empty) defaults
 *     - fallbacks can be defined in templates with "cmsFallback". they get merged in mixin CMSData
 *   - stores validated directus content in vuex
 */

import Sentry from 'plugins/sentry.TARGET';
import defaultsDeep from 'lodash.defaultsdeep';
import { directusLoadContent, directusLoadList } from 'requests/directusLoadContent';

export default (store) => {
  const defaultDirectusResponse = {
    status: '',
    title: '',
    contentbox_1: '',
    contentbox_2: '',
    contentbox_3: '',
    contentbox_4: '',
    contentbox_5: '',
    contentbox_6: '',
    contentbox_7: '',
    contentbox_8: '',
    image_list_1_title: '',
    image_list_1: [],
    image_list_2_title: '',
    image_list_2: [],
    image_list_3_title: '',
    image_list_3: [],
    image_list_4_title: '',
    image_list_4: [],
    json_meta: {
      title: undefined,
      description: undefined,
      canonical: undefined,
    },
  };

  const getDirectusContent = ({
    collection,
    language,
    pageAlias,
    fields,
  }) => directusLoadContent({
    language,
    collection,
    pageAlias,
    fields,
  }).then((directusContent) => {
    if (!directusContent?.content?.data) {
      throw new Error(`missing directus content in ${pageAlias}`);
    }

    // validate directus response and set fallbacks
    const errors = [];
    fields.forEach((field) => {
      switch (field) {
        case 'json_meta':
          // validate json_meta
          try {
            directusContent.content.data.json_meta = JSON.parse(directusContent.content.data.json_meta || '{}');
          } catch (err) {
            errors.push(err);
          }
          // check required fields for json_meta
          if (!directusContent.content.data.json_meta?.title) {
            errors.push(new Error(`missing meta data "title" for ${pageAlias}`));
          }
          if (!directusContent.content.data.json_meta?.description) {
            errors.push(new Error(`missing meta data "description" for ${pageAlias}`));
          }
          if (!directusContent.content.data.json_meta?.canonical) {
            errors.push(new Error(`missing meta data "canonical" for ${pageAlias}`));
          }
          break;
        case 'image_list_1':
        case 'image_list_2':
        case 'image_list_3':
        case 'image_list_4':
          // validate image list array
          if (!Array.isArray(directusContent.content.data[field])) {
            errors.push(new Error(`response invalid for ${field}. should be array of images`));
          }
          break;
        default:
          // validate other fields
          if (!directusContent.content.data[field] && directusContent.content.data[field] !== '') {
            errors.push(new Error(`no response for ${field}`));
          }
          break;
      }
    });

    if (errors.length) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('directus error! request data:', {
          collection,
          pageAlias,
          fields,
        });
      }
      errors.forEach((error) => {
        if (process.env.NODE_ENV === 'production') {
          Sentry.captureException(error);
        } else {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      });
    }

    const storeContent = defaultsDeep(directusContent.content.data, defaultDirectusResponse);
    const vuexKey = `${collection}_${pageAlias}_${language}`;
    store.commit('SET_DIRECTUS_CONTENT', { pageAlias: vuexKey, directusContent: storeContent });
    store.commit('SET_APP_HTTPSTATUS', 200);
    store.commit('SET_USEPAGECACHE', true);
  }).catch((error) => {
    if (process.env.NODE_ENV === 'production') {
      Sentry.captureException(error);
    } else {
      // eslint-disable-next-line no-console
      console.error('directus error.\nrequest data:\n', {
        collection,
        language,
        pageAlias,
        fields,
      }, '\nerror:\n', error);
    }
    const { status = 500 } = error;
    store.commit('SET_APP_HTTPSTATUS', status);
  });

  const getDirectusList = ({
    language,
    collection,
    fields,
    page,
  }) => directusLoadList({
    language,
    collection,
    fields,
    page,
  }).then((directusContent) => {
    if (!directusContent?.content?.data) {
      throw new Error(`missing directus content in ${collection}`);
    }

    const storeContent = store.getters.getDirectusList(collection);

    // init arrays
    if (!storeContent[language]) {
      storeContent[language] = {};
    }
    if (!storeContent[language][page]) {
      storeContent[language][page] = {};
    }

    storeContent[language][page] = directusContent.content.data;
    storeContent[language].meta = directusContent.content.meta;
    store.commit('SET_DIRECTUS_CONTENT', { pageAlias: collection, directusContent: storeContent });
    store.commit('SET_APP_APIHTTPSTATUS', { type: 'directusList', status: 200 });
    store.commit('SET_APP_HTTPSTATUS', 200);
    store.commit('SET_USEPAGECACHE', true);
    return directusContent.content;
  }).catch((error) => {
    if (process.env.NODE_ENV === 'production') {
      Sentry.captureException(error);
    } else {
      // eslint-disable-next-line no-console
      console.error('directus error.\nrequest data:\n', {
        collection,
        language,
        fields,
        page,
      }, '\nerror:\n', error);
    }
    const { status = 500 } = error;
    store.commit('SET_APP_APIHTTPSTATUS', { type: 'directusList', status });
    store.commit('SET_APP_HTTPSTATUS', status);
  });

  return {
    getDirectusContent,
    getDirectusList,
  };
};
